import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Divider,
  TextField,
  MenuItem,
  CircularProgress,
  Stack,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  CheckCircle,
  Pending,
  Cancel,
  AttachFile,
  Save as SaveIcon,
  History as HistoryIcon,
  Notes as NotesIcon,
  Inbox as InboxIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  CardMembership as CardMembershipIcon,
  Warning as WarningIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  MailOutline as MailOutlineIcon,
  Reply as ReplyIcon,
  Sync as SyncIcon,
  SwapHoriz as SwapHorizIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { fetchDemarcheById } from '../redux/slices/demarchesSlice';
import { AppDispatch, RootState } from '../redux/store';
import Layout from '../components/Layout';
import DrawerPanel from '../components/DrawerPanel';
import SousDemarcheForm from '../components/SousDemarcheForm';
import StepProgress from '../components/StepProgress';
import AnefDetails from '../components/AnefDetails';
import DemarcheSyncMigrationModal from '../components/DemarcheSyncMigrationModal';
import api from '../services/api';

const DemarcheDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const { currentDemarche, loading } = useSelector((state: RootState) => state.demarches);
  const [sousDemarches, setSousDemarches] = useState<any[]>([]);
  const [sousDemarcheDrawerOpen, setSousDemarcheDrawerOpen] = useState(false);
  const [editingSousDemarche, setEditingSousDemarche] = useState<any>(null);
  const [notes, setNotes] = useState('');
  const [historique, setHistorique] = useState<any[]>([]);
  const [editingNotes, setEditingNotes] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [monitoringActif, setMonitoringActif] = useState(false);
  const [togglingMonitoring, setTogglingMonitoring] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(fetchDemarcheById(parseInt(id)));
      fetchSousDemarches();
      fetchHistorique();
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (currentDemarche) {
      setNotes(currentDemarche.notes || '');
      setMonitoringActif(currentDemarche.monitoring_actif || false);
    }
  }, [currentDemarche]);

  const fetchSousDemarches = async () => {
    try {
      const response = await api.get(`/demarches/${id}/sous-demarches`);
      setSousDemarches(response.data.data || []);
    } catch (error) {
      console.error('Erreur chargement sous-démarches:', error);
    }
  };

  const fetchHistorique = async () => {
    try {
      const response = await api.get(`/demarches/${id}/historique`);
      setHistorique(response.data.data || []);
    } catch (error) {
      console.error('Erreur chargement historique:', error);
    }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.patch(`/demarches/${id}/notes`, { notes });
      setEditingNotes(false);
      dispatch(fetchDemarcheById(parseInt(id!)));
    } catch (error) {
      console.error('Erreur sauvegarde notes:', error);
      alert('Erreur lors de la sauvegarde des notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleChangeStatut = async (nouveauStatut: string) => {
    if (!currentDemarche) return;

    try {
      await api.put(`/demarches/${id}`, {
        ...currentDemarche,
        statut: nouveauStatut
      });
      dispatch(fetchDemarcheById(parseInt(id!)));
      fetchHistorique();
    } catch (error) {
      console.error('Erreur changement statut:', error);
      alert('Erreur lors du changement de statut');
    }
  };

  const handleDeleteSousDemarche = async (sousDemarcheId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette sous-démarche ?')) {
      return;
    }

    try {
      await api.delete(`/demarches/${id}/sous-demarches/${sousDemarcheId}`);
      fetchSousDemarches();
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleEditSousDemarche = (sousDemarche: any) => {
    setEditingSousDemarche(sousDemarche);
    setSousDemarcheDrawerOpen(true);
  };

  const handleNewSousDemarche = () => {
    setEditingSousDemarche(null);
    setSousDemarcheDrawerOpen(true);
  };

  const handleToggleMonitoring = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setTogglingMonitoring(true);

    try {
      await api.patch(`/demarches/${id}/monitoring`, {
        monitoring_actif: newValue
      });
      setMonitoringActif(newValue);
      dispatch(fetchDemarcheById(parseInt(id!)));
    } catch (error) {
      console.error('Erreur toggle monitoring:', error);
      alert('Erreur lors de la modification du monitoring');
      // Revert the state on error
      setMonitoringActif(!newValue);
    } finally {
      setTogglingMonitoring(false);
    }
  };

  const getStatusColor = (statut: string) => {
    const colors: any = {
      en_attente: 'warning',
      en_cours: 'info',
      termine: 'success',
      annule: 'error',
    };
    return colors[statut] || 'default';
  };

  const getStatusIcon = (statut: string) => {
    switch (statut) {
      case 'termine':
        return <CheckCircle fontSize="small" />;
      case 'annule':
        return <Cancel fontSize="small" />;
      default:
        return <Pending fontSize="small" />;
    }
  };

  // Détermine les étapes de progression basées sur le statut ANEF
  const getProgressSteps = () => {
    const statutAnef = currentDemarche?.anef_statut_actuel?.toUpperCase() || '';
    const dernierTitre = currentDemarche?.anef_dernier_titre || '';

    // Vérifier si c'est un vrai titre délivré (pas une attestation de prolongation)
    const estTitreDefinitif = dernierTitre &&
      !dernierTitre.toLowerCase().includes('attestation') &&
      !dernierTitre.toLowerCase().includes('prolongation') &&
      (
        dernierTitre.toLowerCase().includes('titre') ||
        dernierTitre.toLowerCase().includes('cst') ||
        dernierTitre.toLowerCase().includes('aps') ||
        dernierTitre.toLowerCase().includes('carte')
      );

    const steps = [
      {
        label: 'Dépôt de la demande',
        icon: <InboxIcon />,
        status: 'completed' as const, // Toujours complété si la démarche existe
      },
      {
        label: 'Instruction en cours',
        icon: <RefreshIcon />,
        status: (
          statutAnef.includes('INSTRUCTION') ||
          statutAnef.includes('SOUMIS') ||
          statutAnef.includes('EN_COURS') ||
          statutAnef.includes('VALIDATION')
        ) ? 'current' as const :
        (statutAnef.includes('DECISION') || statutAnef.includes('FAVORABLE') || statutAnef.includes('DEFAVORABLE') || estTitreDefinitif)
          ? 'completed' as const
          : 'future' as const,
      },
      {
        label: 'Décision prise',
        icon: <EmailIcon />,
        status: (
          statutAnef.includes('DECISION') ||
          statutAnef.includes('FAVORABLE') ||
          statutAnef.includes('DEFAVORABLE')
        ) ? 'current' as const :
        estTitreDefinitif ? 'completed' as const : 'future' as const,
      },
      {
        label: 'Titre délivré',
        icon: <CardMembershipIcon />,
        status: estTitreDefinitif ? 'completed' as const : 'future' as const,
      },
    ];

    return steps;
  };

  if (loading || !currentDemarche) {
    return (
      <Layout>
        <Box sx={{ p: 3 }}>
          <Typography>Chargement...</Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Header avec retour */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Link to="/demarches" style={{ textDecoration: 'none' }}>
            <Button startIcon={<ArrowBack />} variant="outlined">
              Retour
            </Button>
          </Link>
          <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
            {currentDemarche.titre}
          </Typography>

          {/* Indicateur compte temporaire */}
          {currentDemarche.anef_numero_demande && !currentDemarche.est_numero_etranger && (
            <Chip
              label="Compte temporaire"
              color="warning"
              icon={<WarningIcon />}
              variant="outlined"
            />
          )}

          {currentDemarche.necessite_intervention && (
            <Chip label="Intervention requise" color="error" />
          )}

          {/* Bouton de synchronisation/migration ANEF */}
          {currentDemarche.anef_numero_demande && (
            <Button
              variant={currentDemarche.est_numero_etranger ? "outlined" : "contained"}
              color={currentDemarche.est_numero_etranger ? "primary" : "warning"}
              startIcon={currentDemarche.est_numero_etranger ? <SyncIcon /> : <SwapHorizIcon />}
              onClick={() => setSyncModalOpen(true)}
            >
              {currentDemarche.est_numero_etranger ? 'Synchroniser' : 'Migrer vers définitif'}
            </Button>
          )}

          {/* Toggle de monitoring automatique ANEF */}
          {currentDemarche.anef_numero_demande && (
            <Tooltip
              title="Active le suivi automatique 3 fois par jour (8h, 14h, 20h) pour détecter les changements de statut, nouvelles notifications et demandes de complément"
              arrow
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={monitoringActif}
                    onChange={handleToggleMonitoring}
                    disabled={togglingMonitoring}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <VisibilityIcon fontSize="small" />
                    Monitoring auto
                  </Box>
                }
              />
            </Tooltip>
          )}
        </Box>

        {/* Informations principales */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3}>
            {/* Colonne gauche */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Client
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <Link to={`/clients/${currentDemarche.client_id}`} style={{ textDecoration: 'none', color: '#1976d2' }}>
                  {currentDemarche.client_nom} {currentDemarche.client_prenom}
                </Link>
              </Typography>

              {currentDemarche.type_demarche_nom && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Type de démarche
                  </Typography>
                  <Chip label={currentDemarche.type_demarche_nom} sx={{ mb: 2 }} />
                </>
              )}

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Statut
              </Typography>
              <TextField
                select
                size="small"
                value={currentDemarche.statut}
                onChange={(e) => handleChangeStatut(e.target.value)}
                sx={{ mb: 2, minWidth: 200 }}
              >
                <MenuItem value="en_attente">En attente</MenuItem>
                <MenuItem value="en_cours">En cours</MenuItem>
                <MenuItem value="termine">Terminé</MenuItem>
                <MenuItem value="annule">Annulé</MenuItem>
              </TextField>

              {currentDemarche.description && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    Description
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {currentDemarche.description}
                  </Typography>
                </>
              )}
            </Grid>

            {/* Colonne droite */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Dates
              </Typography>
              <Box sx={{ mb: 2 }}>
                {currentDemarche.date_depot && (
                  <Typography variant="body2">
                    <strong>Dépôt:</strong> {new Date(currentDemarche.date_depot).toLocaleDateString()}
                  </Typography>
                )}
                {currentDemarche.date_limite && (
                  <Typography variant="body2">
                    <strong>Limite:</strong> {new Date(currentDemarche.date_limite).toLocaleDateString()}
                  </Typography>
                )}
                <Typography variant="body2">
                  <strong>Créé le:</strong> {new Date(currentDemarche.created_at).toLocaleDateString()}
                </Typography>
              </Box>

              {currentDemarche.rappel_actif && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Rappels
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Fréquence:</strong> Tous les {currentDemarche.frequence_rappel_jours} jours
                    </Typography>
                    {currentDemarche.prochain_rappel && (
                      <Typography variant="body2">
                        <strong>Prochain rappel:</strong> {new Date(currentDemarche.prochain_rappel).toLocaleDateString()}
                      </Typography>
                    )}
                  </Box>
                </>
              )}

              {currentDemarche.commentaires && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    Commentaires
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {currentDemarche.commentaires}
                  </Typography>
                </>
              )}
            </Grid>
          </Grid>
        </Paper>

        {/* Section Progression de la démarche ANEF */}
        {currentDemarche.anef_statut_actuel && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Avancement de la démarche
            </Typography>
            <StepProgress steps={getProgressSteps()} />
          </Paper>
        )}

        {/* Section Informations ANEF - Composant détaillé */}
        <AnefDetails demarche={currentDemarche} />

        {/* Section Notes */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NotesIcon /> Notes
            </Typography>
            {!editingNotes ? (
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={() => setEditingNotes(true)}
              >
                Modifier
              </Button>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  onClick={() => {
                    setEditingNotes(false);
                    setNotes(currentDemarche.notes || '');
                  }}
                >
                  Annuler
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={savingNotes ? <CircularProgress size={16} /> : <SaveIcon />}
                  onClick={handleSaveNotes}
                  disabled={savingNotes}
                >
                  Sauvegarder
                </Button>
              </Box>
            )}
          </Box>

          {editingNotes ? (
            <TextField
              fullWidth
              multiline
              rows={6}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ajouter des notes sur cette démarche..."
            />
          ) : (
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-wrap', color: notes ? 'text.primary' : 'text.secondary' }}
            >
              {notes || 'Aucune note'}
            </Typography>
          )}
        </Paper>

        {/* Section Historique */}
        {historique.length > 0 && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <HistoryIcon /> Historique des statuts
            </Typography>

            <Stack spacing={2}>
              {historique.map((item, index) => (
                <Box
                  key={item.id}
                  sx={{
                    display: 'flex',
                    gap: 2,
                    p: 2,
                    bgcolor: 'background.default',
                    borderRadius: 1,
                    borderLeft: 3,
                    borderColor: `${getStatusColor(item.nouveau_statut)}.main`,
                  }}
                >
                  <Box sx={{ minWidth: 140 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {new Date(item.date_changement).toLocaleDateString('fr-FR')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {new Date(item.date_changement).toLocaleTimeString('fr-FR')}
                    </Typography>
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Box sx={{ mb: 0.5 }}>
                      {item.ancien_statut ? (
                        <>
                          <Chip label={item.ancien_statut} size="small" sx={{ mr: 1 }} />
                          →
                          <Chip label={item.nouveau_statut} size="small" color={getStatusColor(item.nouveau_statut)} sx={{ ml: 1 }} />
                        </>
                      ) : (
                        <Chip label={item.nouveau_statut} size="small" color={getStatusColor(item.nouveau_statut)} />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Par: {item.user_nom} {item.user_prenom}
                    </Typography>
                    {item.commentaire && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {item.commentaire}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Stack>
          </Paper>
        )}

        {/* Sous-démarches */}
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h5" component="h2">
              Sous-démarches ({sousDemarches.length})
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleNewSousDemarche}
            >
              Nouvelle sous-démarche
            </Button>
          </Box>

          {sousDemarches.length === 0 ? (
            <Alert severity="info">
              Aucune sous-démarche pour le moment. Cliquez sur "Nouvelle sous-démarche" pour en ajouter une.
            </Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Titre</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Date limite</TableCell>
                    <TableCell>Fichiers</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sousDemarches.map((sousDemarche) => (
                    <TableRow key={sousDemarche.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {sousDemarche.titre}
                        </Typography>
                        {sousDemarche.description && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {sousDemarche.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={sousDemarche.statut.replace(/_/g, ' ')}
                          color={getStatusColor(sousDemarche.statut)}
                          size="small"
                          icon={getStatusIcon(sousDemarche.statut)}
                        />
                      </TableCell>
                      <TableCell>
                        {sousDemarche.date_limite
                          ? new Date(sousDemarche.date_limite).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {sousDemarche.nombre_fichiers > 0 ? (
                          <Chip
                            icon={<AttachFile />}
                            label={sousDemarche.nombre_fichiers}
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditSousDemarche(sousDemarche)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteSousDemarche(sousDemarche.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Panel latéral pour sous-démarche */}
        <DrawerPanel
          open={sousDemarcheDrawerOpen}
          onClose={() => {
            setSousDemarcheDrawerOpen(false);
            setEditingSousDemarche(null);
          }}
          title={editingSousDemarche ? 'Modifier la sous-démarche' : 'Nouvelle sous-démarche'}
          width={600}
        >
          <SousDemarcheForm
            demarcheId={parseInt(id!)}
            sousDemarche={editingSousDemarche}
            onSuccess={() => {
              setSousDemarcheDrawerOpen(false);
              setEditingSousDemarche(null);
              fetchSousDemarches();
            }}
            onCancel={() => {
              setSousDemarcheDrawerOpen(false);
              setEditingSousDemarche(null);
            }}
          />
        </DrawerPanel>

        {/* Modal de synchronisation/migration ANEF */}
        {currentDemarche && (
          <DemarcheSyncMigrationModal
            open={syncModalOpen}
            onClose={() => setSyncModalOpen(false)}
            demarcheId={currentDemarche.id}
            isCompteTemporaire={!currentDemarche.est_numero_etranger}
            onSuccess={() => {
              dispatch(fetchDemarcheById(parseInt(id!)));
              fetchHistorique();
            }}
          />
        )}
      </Box>
    </Layout>
  );
};

export default DemarcheDetails;
